import { debounce } from 'lodash';
import { analyticsAPI } from './AnalyticsAPI';
import { trackingTrace } from './TrackingDebug';
import type { PageChangeEvent, PageViewEvent, TrackingPayload } from '@publicala/delfino';

const DEBOUNCE_DELAY_MS = 1000;

/**
 * Extract the trailing run of digits from a pagebreak id. Handles both our
 * own injected `pla-pb-N` prefix and publisher-emitted variants like `p23`,
 * `page_42`, or `chapter1-pb-3` (last digit run wins, so the inner `1` from
 * `chapter1` is correctly ignored). Returns `null` when the id has no digits.
 */
function extractNumericId(id: string): number | null {
    const matches = id.match(/\d+/g);
    if (!matches || matches.length === 0) return null;
    return Number(matches[matches.length - 1]);
}

/**
 * A printed-page marker the reader just entered (from PagebreakTracker).
 */
export interface PagebreakEnteredEntry {
    title: string | null;
    id: string;
}

/**
 * Batch of marker entries plus the locator at emission time.
 */
export interface PagebreaksEnteredEvent {
    entries: PagebreakEnteredEntry[];
    locator?: unknown;
}

/**
 * Suppression signal from the EPUB viewer around programmatic navigation.
 * - `silent`: reflows (font size, sidebar, resize) — nothing the user did;
 *   every page-change in the window is dropped.
 * - `emit-final`: user-intended jumps (progress bar, TOC, note source) — the
 *   window collapses to exactly one page-change with the final destination.
 */
export interface TrackingSuppressionSignal {
    phase: 'begin' | 'end';
    mode?: 'silent' | 'emit-final';
}

/**
 * Environment accessors injected by the reader shell. All lazy: they are
 * consulted per event, so registration order against store hydration does
 * not matter.
 */
export interface TrackingEnvironment {
    /** EPUB with printed-page markers (host-provided hasPagebreaks). */
    isPagebreakMode?: () => boolean;
    /** Current publication is an EPUB (fallback baseline only applies there). */
    isEpub?: () => boolean;
    /** Reader still loading/restoring position — renders are not navigation. */
    isLoading?: () => boolean;
}

/**
 * Whether page-changes should be emitted from printed-page-marker entries
 * (`pagebreaksEntered`) instead of the navigation fallback (`pageChanged`).
 *
 * Fixed-layout is excluded even when the host reports markers: the
 * PagebreakTracker never observes FXL frames, so the marker branch has no
 * event source there — routing FXL to it silences page-changes entirely.
 * FXL stays on the navigation fallback, where position is a reliable signal
 * (one position per spine file, i.e. per page; double-page spreads advance
 * it too).
 */
export function isPagebreakEmissionMode(state: {
    isEpub: boolean;
    hasPagebreaks: boolean;
    isFixedLayout: boolean;
}): boolean {
    return state.isEpub && state.hasPagebreaks && !state.isFixedLayout;
}

/**
 * Vue event bus interface for listening to page change events.
 * This is specific to Volpe's Vue implementation.
 */
export interface TrackingEventBus {
    $on(event: 'pageChanged', callback: (e: PageChangeEvent) => void): void;
    $on(event: 'pageView', callback: (e: PageViewEvent) => void): void;
    $on(event: string, callback: (...args: unknown[]) => void): void;
}

/**
 * Listens to user events and sends them to analytics.
 * Events: page-change, page-view, tab-hidden, tab-visible
 *
 * page-change routing: a page_turn represents the reader entering a printed
 * page, so EPUBs with markers emit from `pagebreaksEntered`
 * (PagebreakTracker transitions) and ignore `pageChanged` entirely — that
 * bus event fires on every Readium render (handshakes, resizes, CSS
 * mutations), which is what produced phantom turns with no real navigation
 * (opening, tab return, reflows, double-emitting jumps). Publications
 * without markers keep the `pageChanged` path behind dedup/suppression
 * guards.
 */
const DEFAULT_ENVIRONMENT: Required<TrackingEnvironment> = {
    isPagebreakMode: () => false,
    isEpub: () => false,
    isLoading: () => false,
};

class TrackingEventsListener {
    private eventBus: TrackingEventBus | null = null;
    private isInitialized = false;
    private environment: Required<TrackingEnvironment> = DEFAULT_ENVIRONMENT;

    private suppressionMode: 'silent' | 'emit-final' | null = null;
    /** Deferred final page-change to emit when an emit-final window closes. */
    private pendingFinalEmit: (() => void) | null = null;

    /** Last page number sent through the fallback path (position dedup). */
    private lastFallbackPage: number | null = null;
    /**
     * The fallback's opening baseline: an EPUB's first `pageChanged` is the
     * initial render/restore, not navigation. PDFs skip this — their page
     * watcher only fires on real changes, so a baseline would swallow the
     * user's first actual page turn.
     */
    private fallbackBaselinePending = true;

    private debouncedFallbackPageChange: ReturnType<typeof debounce> | null = null;

    /**
     * Initializes the listener with an event bus and registers all event handlers.
     * @param eventBus - Vue event bus for listening to page changes
     * @param environment - Lazy accessors into the reader state
     */
    init(eventBus: TrackingEventBus, environment: TrackingEnvironment = {}): void {
        if (this.isInitialized) return;

        this.eventBus = eventBus;
        this.environment = { ...DEFAULT_ENVIRONMENT, ...environment };
        this.registerEventListeners();
        this.isInitialized = true;
    }

    /**
     * Registers all event listeners for page changes and visibility changes.
     */
    private registerEventListeners(): void {
        if (!this.eventBus) return;

        // Marker-entry turns arrive pre-deduped and batch-shaped from
        // PagebreakTracker; no debounce — a screen revealing two markers is
        // legitimately two turns (contract: all markers crossed, DOM order).
        this.eventBus.$on('pagebreaksEntered', (event: PagebreaksEnteredEvent) => {
            this.handlePagebreaksEntered(event);
        });

        this.eventBus.$on('trackingSuppression', (signal: TrackingSuppressionSignal) => {
            this.handleSuppressionSignal(signal);
        });

        this.debouncedFallbackPageChange = debounce(
            (event: PageChangeEvent) => {
                this.handleFallbackPageChange(event);
            },
            DEBOUNCE_DELAY_MS,
            { leading: true, trailing: true }
        );

        this.eventBus.$on('pageChanged', (event: PageChangeEvent) => {
            // Marker mode emits turns from pagebreaksEntered; the render-driven
            // bus event must not reach analytics at all.
            if (this.environment.isPagebreakMode()) return;
            this.debouncedFallbackPageChange?.(event);
        });

        // No debounce: view confirmations are already deduped at the source
        // (the tracker confirms a page once per on-screen stay), and a screen
        // holding several printed pages legitimately confirms several views —
        // a leading+trailing window here silently swallowed the middle ones.
        this.eventBus.$on('pageView', (event: PageViewEvent) => {
            this.trackPageView(event);
        });

        document.addEventListener(
            'visibilitychange',
            () => {
                if (document.hidden) {
                    this.trackTabHidden();
                } else {
                    this.trackTabVisible();
                }
            },
            false
        );
    }

    /**
     * Marker-entry turns (EPUBs with pagebreaks).
     */
    private handlePagebreaksEntered(event: PagebreaksEnteredEvent): void {
        if (!this.environment.isPagebreakMode()) return;
        if (event.entries.length === 0) return;

        if (this.suppressionMode === 'silent') {
            trackingTrace('listener:turns-suppressed', { mode: 'silent', ids: event.entries.map(e => e.id) });
            return;
        }

        if (this.suppressionMode === 'emit-final') {
            // A jump lands where it lands: the first marker of the LAST batch
            // is the destination page. Later batches overwrite earlier ones
            // (the intermediate chapter-open landing of a progress-bar jump).
            const entry = event.entries[0];
            trackingTrace('listener:turns-deferred', { mode: 'emit-final', id: entry.id });
            this.pendingFinalEmit = () => this.trackPagebreakTurn(entry, event.locator);
            return;
        }

        for (const entry of event.entries) {
            this.trackPagebreakTurn(entry, event.locator);
        }
    }

    /**
     * Emits one page-change for a printed-page marker entry. `currentPage`
     * follows the page-view numbering rule (title first, id digits as
     * fallback, null when neither parses) on purpose: page-change and
     * page-view must report the same printed-page scale, or downstream
     * consumers see two unrelated numbering systems for the same screen.
     */
    private trackPagebreakTurn(entry: PagebreakEnteredEntry, locator?: unknown): void {
        const numericId = extractNumericId(entry.id);
        const parsedTitle = entry.title ? Number(entry.title) : NaN;
        const currentPage = Number.isFinite(parsedTitle) ? parsedTitle : numericId;

        const payload: TrackingPayload = {
            currentPage: currentPage ?? null,
            extraPayload: {
                pagebreak: {
                    id: entry.id,
                    numericId,
                    title: entry.title ?? undefined,
                },
                ...(locator ? { epub: locator } : {}),
            },
        };

        trackingTrace('listener:emit-page-change', {
            page: currentPage ?? null,
            pagebreakId: entry.id,
            path: 'marker',
        });
        analyticsAPI.track('page-change', payload);
    }

    /**
     * Fallback page-change path (PDFs, EPUBs without markers). Debounced
     * upstream; here the render-driven noise gets filtered:
     * - loading window: renders during open/restore are not navigation
     * - EPUB baseline: the first event is the opening position
     * - position dedup: re-renders of the same page (tab return, handshakes)
     * - suppression: programmatic navigation windows from the viewer
     */
    private handleFallbackPageChange(event: PageChangeEvent): void {
        const pageNumber = event.pageNumber ?? null;

        if (this.environment.isLoading()) {
            this.consumeFallbackBaseline(pageNumber);
            return;
        }

        if (this.environment.isEpub() && this.fallbackBaselinePending) {
            this.consumeFallbackBaseline(pageNumber);
            return;
        }
        this.fallbackBaselinePending = false;

        if (pageNumber != null && pageNumber === this.lastFallbackPage) return;

        if (this.suppressionMode === 'silent') {
            this.lastFallbackPage = pageNumber ?? this.lastFallbackPage;
            return;
        }

        if (this.suppressionMode === 'emit-final') {
            this.pendingFinalEmit = () => {
                this.lastFallbackPage = pageNumber ?? this.lastFallbackPage;
                this.trackPageChange(event);
            };
            return;
        }

        this.lastFallbackPage = pageNumber ?? this.lastFallbackPage;
        this.trackPageChange(event);
    }

    /** Fix the fallback position anchor without emitting. */
    private consumeFallbackBaseline(pageNumber: number | null): void {
        this.fallbackBaselinePending = false;
        if (pageNumber != null) {
            this.lastFallbackPage = pageNumber;
        }
    }

    /**
     * Opens/closes a suppression window (signaled by the EPUB viewer around
     * programmatic navigation and reflows). Pending debounced fallback calls
     * are cancelled at both edges so a stale trailing emission cannot leak
     * around the window.
     */
    private handleSuppressionSignal(signal: TrackingSuppressionSignal): void {
        this.debouncedFallbackPageChange?.cancel();

        trackingTrace('listener:suppression', { phase: signal.phase, mode: signal.mode ?? null });

        if (signal.phase === 'begin') {
            this.suppressionMode = signal.mode ?? 'silent';
            this.pendingFinalEmit = null;
            return;
        }

        const pending = this.pendingFinalEmit;
        this.suppressionMode = null;
        this.pendingFinalEmit = null;
        pending?.();
    }

    /**
     * Tracks a page change event.
     * @param event - Page change data (pageNumber for PDF, extraPayload.epub for EPUB)
     */
    private trackPageChange(event: PageChangeEvent): void {
        const payload: TrackingPayload = {
            currentPage: event.pageNumber ?? null,
            extraPayload: event.extraPayload,
        };

        trackingTrace('listener:emit-page-change', { page: event.pageNumber ?? null, path: 'fallback' });
        analyticsAPI.track('page-change', payload);
    }

    /**
     * Tracks a confirmed page view (printed page visible for the configured
     * delay in EPUB content, or inferred page-view in fixed-layout / PDF).
     *
     * For reflowable EPUBs that emit a real pagebreak element, the raw `id`
     * and parsed `numericId` ride along in `extraPayload.pagebreak` so the
     * host can distinguish publisher-printed pages from inferred page indices
     * without parsing prefixes.
     */
    private trackPageView(event: PageViewEvent): void {
        const payload: TrackingPayload = {
            currentPage: event.title ? Number(event.title) : null,
            extraPayload: event.id
                ? {
                      pagebreak: {
                          id: event.id,
                          numericId: extractNumericId(event.id),
                          title: event.title,
                      },
                  }
                : undefined,
        };

        trackingTrace('listener:emit-page-view', { page: payload.currentPage, pagebreakId: event.id ?? null });
        analyticsAPI.track('page-view', payload);
    }

    /**
     * Tracks when user switches away from the tab.
     */
    private trackTabHidden(): void {
        analyticsAPI.track('tab-hidden', {});
    }

    /**
     * Tracks when user returns to the tab.
     */
    private trackTabVisible(): void {
        analyticsAPI.track('tab-visible', {});
    }
}

export const trackingEventsListener = new TrackingEventsListener();
